import { useState } from "react";
import { Link } from "react-router-dom";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import PdfPreviewModal from "../../../../../components/Modals/PdfPreviewModal";
import { btn } from "../../../../../components/Button/buttonStyles";
import { pdfTemplateName } from "../../../../../utils/constants/pdfTemplates";
import { clientName, personName, formatDateTime, formatLanguagePair } from "../../quotationFormat";
import { Card, Field } from "./DetailFields";

const QuotationDetailsCard = ({ quotation, locked, onEdit, onDelete }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const quoteLabel = quotation.quotation_code || quotation.id;

  const languagePairs = quotation.languagePairs?.length ? (
    <div className="flex flex-wrap gap-2">
      {quotation.languagePairs.map((lp) => (
        <span key={lp.id} className="border rounded-md px-2 py-0.5 text-xs bg-gray-50 text-gray-700">
          {formatLanguagePair(lp)}
        </span>
      ))}
    </div>
  ) : (
    ""
  );

  return (
    <section>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-bold text-gray-900">
          Quotation Details
          {quotation.quotation_code && (
            <span className="ml-2 text-sm font-medium text-gray-500">{quotation.quotation_code}</span>
          )}
        </h2>
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => setPdfOpen(true)} className={btn("secondary", "sm")}>
            Download PDF
          </button>
          <button onClick={onEdit} className={btn("primary", "sm")}>
            Update
          </button>
          {/* A quotation already turned into a project cannot be deleted. */}
          {!locked && (
            <button onClick={() => setConfirmOpen(true)} className={btn("danger", "sm")}>
              Delete
            </button>
          )}
        </div>
      </div>

      <Card>
        <Field
          label="Client"
          value={
            quotation.client?.id ? (
              <Link
                to={`/clients/${quotation.client.id}`}
                className="text-brand-600 hover:text-brand-800 underline font-medium"
              >
                {clientName(quotation.client) || "—"}
              </Link>
            ) : (
              ""
            )
          }
        />
        <Field label="Contact Person" value={personName(quotation.contactPerson)} />
        <Field label="Name" value={quotation.quotation_name} />
        <Field label="Language Pairs" value={languagePairs} />
        <Field label="Specialization" value={quotation.specialization?.name} />
        <Field label="Start At" value={formatDateTime(quotation.start_at)} />
        <Field label="Delivery At" value={formatDateTime(quotation.deadline_at)} />
        <Field
          label="Instructions"
          value={
            quotation.instructions ? (
              <div
                className="prose prose-sm max-w-none text-gray-900"
                dangerouslySetInnerHTML={{ __html: quotation.instructions }}
              />
            ) : (
              ""
            )
          }
        />
        <Field label="Expire At" value={formatDateTime(quotation.expire_at)} />
        <Field label="PDF Template" value={pdfTemplateName(quotation.pdf_template_id)} />
        <Field
          label="Internal Note"
          value={
            quotation.internal_note ? (
              <span className="whitespace-pre-line">{quotation.internal_note}</span>
            ) : (
              ""
            )
          }
        />
        <Field label="Created At" value={formatDateTime(quotation.createdAt)} />
      </Card>

      {confirmOpen && (
        <ConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to delete this quotation? Its files, receivables and taxes are deleted with it."
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            onDelete();
          }}
          confirmText="Delete"
        />
      )}

      {pdfOpen && (
        <PdfPreviewModal
          title={`Quote ${quoteLabel}`}
          url={`/quotations/${quotation.id}/pdf`}
          fileName={`Quote ${quoteLabel}.pdf`}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </section>
  );
};

export default QuotationDetailsCard;
